import { ReactNode, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import { useContainerDimensions } from '../shared/component-height/component-dimensions.hooks';
import { ComponentAspectRatio } from '../shared/component-height/component-dimensions';
import { ComponentAbsoluteWrapper } from '../shared/component-height/component-dimensions.style';
import { Dimensions } from '../shared/component-height/component-dimensions.constants';
import { CommonPrintProps } from 'approot/activity-builder/activity-builder.constants';
import { getQrCodeHeight } from './print.utils';
import { PrintQrContainer, PrintQrContainerLink } from './print.style';
import * as Styled from './print-stimulus.style';

export const PrintStimulusAspectRatio = ({
  dimensions,
  link,
  coverImage,
  isPrintPreview,
}: {
  dimensions: Dimensions;
  link?: string | null;
  coverImage?: string | null;
} & CommonPrintProps) => {
  const { style, elementDimensions, setContainerElement } =
    useContainerDimensions({
      aspectRatio: 16 / 9,
    });

  let qrCodeHeight = useMemo(
    () => getQrCodeHeight(elementDimensions),
    [elementDimensions]
  );

  return (
    <ComponentAspectRatio dimensions={dimensions}>
      <ComponentAbsoluteWrapper ref={setContainerElement}>
        <Styled.PrintStimulusAspectRatioInner
          $backgroundImg={coverImage}
          $fullBorderRadius
          style={style}
        >
          {link && isPrintPreview && (
            <PrintQrContainer
              style={{
                width: qrCodeHeight,
                height: qrCodeHeight,
              }}
            >
              <QRCodeSVG value={link} />
            </PrintQrContainer>
          )}
          {link && !isPrintPreview && (
            <PrintQrContainerLink
              href={link}
              style={{
                width: qrCodeHeight,
                height: qrCodeHeight,
              }}
            >
              <QRCodeSVG value={link} />
            </PrintQrContainerLink>
          )}
        </Styled.PrintStimulusAspectRatioInner>
      </ComponentAbsoluteWrapper>
    </ComponentAspectRatio>
  );
};

export const PrintStimulusLink = ({
  primaryText,
  link,
  details,
  isPrintPreview,
}: {
  primaryText: string;
  link: string;
  details?: ReactNode;
} & CommonPrintProps) => (
  <Styled.PrintStimulusLink>
    {isPrintPreview && (
      <Styled.PrintStimulusImageContainer>
        <QRCodeSVG value={link} />
      </Styled.PrintStimulusImageContainer>
    )}
    {!isPrintPreview && (
      <Styled.PrintStimulusImageContainerLink href={link}>
        <QRCodeSVG value={link} />
      </Styled.PrintStimulusImageContainerLink>
    )}
    <Styled.PrintStimulusCol>
      <Styled.PrintStimulusText>{primaryText}</Styled.PrintStimulusText>
      <Styled.PrintStimulusLinkWrapper>
        {details && details}
      </Styled.PrintStimulusLinkWrapper>
    </Styled.PrintStimulusCol>
  </Styled.PrintStimulusLink>
);

export const PrintStimulusLinkNarrow = ({
  primaryText,
  link,
  details,
  isPrintPreview,
}: {
  primaryText: string;
  link: string;
  details?: ReactNode;
} & CommonPrintProps) => (
  <Styled.PrintStimulusLinkNarrow>
    {isPrintPreview && (
      <Styled.PrintStimulusImageContainerNarrow>
        <QRCodeSVG value={link} width={64} height={64} />
      </Styled.PrintStimulusImageContainerNarrow>
    )}
    {!isPrintPreview && (
      <Styled.PrintStimulusImageContainerNarrowLink href={link}>
        <QRCodeSVG value={link} width={64} height={64} />
      </Styled.PrintStimulusImageContainerNarrowLink>
    )}
    <Styled.PrintStimulusText>{primaryText}</Styled.PrintStimulusText>
    <Styled.PrintStimulusLinkWrapper>
      {details && details}
    </Styled.PrintStimulusLinkWrapper>
  </Styled.PrintStimulusLinkNarrow>
);
